/**
 * `AW4` — borde de LECTURA de la tabla de conversaciones contra `admin-web`.
 *
 * `contracts/conversation` fija el puerto (`ConversationStore.listar`), la forma
 * de la fila y el filtro; este archivo fija el cableado HTTP: la ruta, los
 * nombres de los parámetros de query y el revival de fechas. Sigue el mismo
 * precedente que `src/proveedor-datos.ts`: el consumidor decide cómo pedir.
 *
 * No existe `contracts/conversation/http.ts`; el acuerdo de cable vive acá, del
 * lado de la pantalla, y se declara en el informe.
 */
import type {
  DiaDeChile,
  FilaDeMensaje,
  IdConversacion,
  MensajeListado,
  ResultadoDeMensajes,
  TamanoDePagina,
} from "@contracts/conversation/index.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"
import { raizDelBrazo } from "../brazo-del-cable.ts"
import { FILAS_POR_PAGINA } from "../paginado.tsx"
import { descargarArchivo } from "../descargar-archivo.ts"

/**
 * ⭐⭐ ⟦el-tamano-de-pagina-es-uno-de-CINCO⟧ · `R3-SAN-UI` · **Estrecha un entero
 * cualquiera a uno de los cinco tamaños del contrato, o dice que NO.**
 *
 * `contracts/conversation` (`R3-CCV1`) cerró `ConsultaDeMensajes.tamanoDePagina`
 * a `TamanoDePagina = 10 | 50 | 100 | 200 | 500`, y lo explica: *«alguien tiene
 * que ESTRECHAR un entero que llegó de un querystring a uno de estos cinco; con
 * el campo tipado, ese estrechado es obligatorio y `tsc` dice dónde falta»*. Esta
 * función es ese estrechado del lado de la SPA, escrito UNA vez.
 *
 * ⛔ **No hay `?? 10` acá, y es la razón de que devuelva `null`.** Un default
 * silencioso convierte una instancia mal configurada en una pantalla que pagina
 * distinto de lo que dice su `configuracion.json`, sin que nada se queje — el
 * `[]`/`0`/`""` servido como verdad que `specs/R3-0-saneamiento/spec.md` §La
 * regla 3 prohíbe. Quien llama decide, y las dos pantallas que llaman fallan
 * cerradas por su camino de error.
 *
 * ⭐ **La lista es la de `R3-UI0` y no una sexta copia.** `FILAS_POR_PAGINA` vive
 * en `paginado.tsx` (`R3-UI0`, «Filas por página»); acá sólo se LEE. Y el tipo de
 * retorno es el del contrato, así que **el día que las dos listas diverjan `tsc`
 * se cae acá** en vez de dejar la SPA pidiendo un tamaño que el borde rechaza con
 * 4xx. (`apps/management-ui` no puede importar un VALOR de un contrato,
 * `ADR-0265` ⓑ: por eso se lee la de la SPA y se ata con el TIPO del contrato.)
 *
 * ⛔ **Que el tamaño deje de salir de `config.pageSize` es de `R3-UI1`**
 * (`specs/R3-F1-conversaciones-apps/etapas/r3-ui1.md:53`, textual: *«el tamaño ya
 * no sale de `config.pageSize`»*). Esta etapa no lo cambia: sólo impide que un
 * valor fuera de los cinco viaje.
 */
export function tamanoDePaginaDelContrato(valor: number): TamanoDePagina | null {
  return FILAS_POR_PAGINA.find((uno) => uno === valor) ?? null
}

/**
 * El texto con el que las dos pantallas que leen mensajes fallan cerradas cuando
 * `config.pageSize` no es uno de los cinco. Nombra a la etapa dueña, como pide la
 * Regla 3 del saneamiento.
 */
export const TAMANO_DE_PAGINA_FUERA_DE_ESCALA =
  "El tamaño de página configurado no es uno de los que el servidor acepta (10, 50, 100, 200 o 500). La etapa R3-UI1 pasa «Filas por página» a la pantalla."

/**
 * ⭐⭐ `R3-UI1` · ⟦los-dias-viajan-como-dias⟧ · **EL FILTRO TAL COMO LO MANDA LA
 * PANTALLA, Y POR QUÉ NO ES `FiltroDeMensajes`.**
 *
 * RT 3 (`PRD_R3.md:890`), textual: Desde y Hasta *«viajan al servidor como fechas
 * sin hora, **sin depender del formato en que las muestre el navegador**»*, y
 * `contracts/conversation` lo nombra con dos cosas: `FORMA_DEL_DIA` (`aaaa-mm-dd`)
 * y `DiaDeChile`.
 *
 * ⛔ **`FiltroDeMensajes` NO sirve de este lado, y el propio contrato lo dice.**
 * `FiltroDeMensajes.desde` es `Date | null` y su docblock aclara:
 * *«**YA ES UN INSTANTE, y el día lo convirtió el BORDE** … lo que viaja en el
 * pedido es un `DiaDeChile`»*, y `DiaDeChile` agrega *«**No aparece en
 * `FiltroDeMensajes`, a propósito**: el filtro que llega acá ya son instantes»*.
 * O sea: `FiltroDeMensajes` es lo que recibe el MÓDULO, después de que
 * `admin-web` (`R3-AW1`) convierta el día a las 00:00:00/23:59:59 de
 * `ZONA_DE_LOS_DIAS`. Este tipo es lo que MANDA la pantalla, que es la otra
 * punta del mismo cable.
 *
 * ⛔ **Por qué NO se arregla construyendo el `Date` acá con un `-03:00`**: es el
 * `rationale_hint` textual del guard RT 3 —*«se rompe en el horario de verano de
 * Chile y con el navegador en otra zona (la demo corre con el navegador en UTC,
 * PRD:1028)»*—. La zona se nombra por su identificador IANA y la conversión la
 * hace UN lado, el que tiene la zona: el servidor.
 *
 * ⚠️ El acuerdo de cable vive acá y no en `contracts/conversation/http.ts`, que no
 * existe: mismo criterio que el resto de este archivo, declarado en su docblock.
 */
export interface FiltroDeLaPantalla {
  /** El valor crudo del `<input type="date">`: un día, `aaaa-mm-dd`. `null` = no filtra. */
  readonly desde: DiaDeChile | null
  /** Ídem `desde`. El servidor lo lleva a las 23:59:59 de ese día en Chile. */
  readonly hasta: DiaDeChile | null
  /**
   * Tal cual se tipeó. ⟦el-telefono-es-opaco⟧ y `PRD_R3.md:691`: *«La pantalla no
   * corrige lo escrito»*; quien lo limpia es `admin-web` (`R3-AW1`), una sola vez.
   */
  readonly telefono: string | null
  /**
   * ⭐ `R3-UI1` · El filtro Contenido, **recortado en los extremos y `null` cuando
   * no queda nada** — el contrato pide el recorte de quien llama, para que el
   * módulo no tenga que decidir si `"   "` filtra (`PRD_R3.md:692`: *«Los espacios
   * de los extremos no cuentan, y un campo con solo espacios no filtra»*).
   */
  readonly contenido: string | null
}

/** Lo que la tabla pide. La forma de `ConsultaDeMensajes`, con el filtro de la PANTALLA. */
export interface ConsultaDeLaPantalla {
  readonly filtro: FiltroDeLaPantalla
  readonly pagina: number
  readonly tamanoDePagina: TamanoDePagina
}

export type BordeConversaciones = {
  readonly listar: (consulta: ConsultaDeLaPantalla) => Promise<ResultadoDeMensajes>
  /**
   * `AW6` — pide el archivo al borde, no lo arma. El botón «Descargar CSV»
   * llama a esto con el filtro que la tabla tiene APLICADO y sin página; el
   * borde arma la ruta de descarga y la baja con la primitiva `descargarArchivo`.
   * `admin-web` contesta con `Content-Disposition: attachment` y el nombre sale
   * del servidor.
   *
   * ⭐ `R4-UIC1` · **devuelve `Promise<void>`** (antes `void`): resuelve cuando el
   * archivo bajó y rechaza si `descargarArchivo` lanza, para que el botón pueda
   * mostrar «Descargando…» mientras está en vuelo y su aviso de fallo si rechaza
   * (CA-13/CA-14). Una navegación de documento no se podía esperar ni fallar.
   */
  readonly descargar: (filtro: FiltroDeLaPantalla) => Promise<void>
  /**
   * ⭐⭐ `AW30` — **cierra la sesión vigente del hilo, y contesta cuál cerró.**
   *
   * ⛔ **Vive acá y no en un borde nuevo.** El simulador es el que aprieta el
   * botón, pero un `borde-cierre.ts` del lado del simulador sería una **segunda
   * declaración del mismo cable** contra `admin-web` —la ruta, el segmento, la
   * cookie, la forma del cuerpo—: la misma regla con la que `AW28` reusó este
   * borde y `AW18` reusó el del padrón. El día que `admin-web` renombre el
   * segmento, se rompe UN lugar y no dos que se desincronizan en silencio.
   *
   * ⭐ **Se identifica por `id_conversacion` y no por participante.** El puerto
   * recibe el hilo largo (`apps/admin-web/src/cierre.ts`
   * ⟦el-hilo-viene-en-la-ruta⟧) y la pantalla ya lo tiene:
   * `FilaDeMensaje.id_conversacion` viaja en cada item de `listar`. ⇒ **ningún
   * teléfono cruza este cable para cerrar.**
   *
   * ⚠️⛔ **`null` NO SE PUEDE INTERPRETAR, Y QUIEN LO DIBUJE TAMPOCO.** El borde
   * de `admin-web` lo dice textual: devuelve `null` tanto para «ese hilo no tenía
   * sesión abierta» **como** para «ese hilo no existe», porque `cerrarSesion` **no
   * tiene variante de ausencia**. ⇒ acá no se traduce a un `boolean`, no se
   * convierte en una excepción y no se le adjudica causa: se devuelve el `null`
   * tal cual, para que la pantalla pueda decir lo único afirmable —**no se cerró
   * ninguna sesión**— sin elegir cuál de las dos fue.
   */
  readonly cerrarSesionVigente: (id_conversacion: IdConversacion) => Promise<string | null>
}

export const RECURSO_CONVERSACIONES = "conversaciones"

/**
 * ⭐ `AW30` — el segmento literal del sub-recurso de cierre, el mismo que
 * `apps/admin-web/src/cierre.ts` exporta como `CIERRE_SEGMENT`.
 *
 * La ruta tiene **tres** segmentos —`conversaciones/{id}/cierre`—, así que no
 * choca con `conversaciones/descarga`, que tiene dos.
 */
export const SEGMENTO_CIERRE = "cierre"

/**
 * `AW6` — la ruta de descarga. La declaró `AP9` en su informe:
 * `GET {raiz}/conversaciones/descarga` con query ~~`desde`/`hasta`/`telefono`~~
 * ⭐ `R3-UI1` · **los CUATRO campos del filtro** (sin `page`/`pageSize`). El
 * acuerdo de cable vive acá, como en `listar`.
 */
export const RECURSO_DESCARGA_CONVERSACIONES = "conversaciones/descarga"

/**
 * La fila tal como viaja en JSON. `fecha_hora` es un `Date` en el contrato, pero
 * por el cable llega como string ISO 8601 (`JSON.stringify` de un `Date`).
 */
type FilaEnLaRed = Omit<FilaDeMensaje, "fecha_hora"> & { readonly fecha_hora: string }

/**
 * El listado tal como viaja en JSON.
 */
type MensajeListadoEnLaRed = Omit<MensajeListado, "fila"> & { readonly fila: FilaEnLaRed }

/**
 * El resultado tal como viaja en JSON.
 */
type ResultadoEnLaRed = Omit<ResultadoDeMensajes, "items"> & {
  readonly items: readonly MensajeListadoEnLaRed[]
}

/**
 * Reconstruye el `Date` que el JSON aplanó a string. `new Date` sobre un valor
 * ilegible devuelve `Invalid Date` y no lanza; la fila sigue mostrándose.
 */
function revivir(listado: MensajeListadoEnLaRed): MensajeListado {
  return {
    ...listado,
    fila: {
      ...listado.fila,
      fecha_hora: new Date(listado.fila.fecha_hora),
    },
  }
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * ⭐⭐ `R3-UI1` · **ESCRIBE EL FILTRO EN UNA `URL`, UNA SOLA VEZ PARA LAS DOS
 * RUTAS.** ⟦el-filtro-se-escribe-una-vez⟧.
 *
 * ⛔ **Existe porque el defecto que R3 vino a cerrar fue exactamente ÉSTE: dos
 * copias del mismo escritor que se separaron.** Hasta el 28/9/2026 `listar` y
 * `construirRutaDeDescarga` escribían el filtro cada una por su cuenta, y cuando
 * `R3-CCV1` sumó el cuarto campo **la descarga se quedó con tres**: el cierre 0b
 * §⑧.5 lo midió, *«hoy da igual porque `contenido` es siempre `null`; el día que
 * la pantalla tenga el campo **el CSV se baja sin ese filtro**»*, que es
 * `R3-F1-CA-67` en rojo sin un solo error. Con un escritor, el quinto campo entra
 * en los dos lados o en ninguno.
 *
 * `null` no se escribe: de este lado «sin filtro» es **no mandar el parámetro**, y
 * el criterio «sin filtro se escribe, no se omite» es del lado de `admin-web`.
 */
function escribirElFiltro(url: URL, filtro: FiltroDeLaPantalla): void {
  /*
   * ⛔ La lista es una sola y se recorre: un `if` por campo es la forma en la que
   * el cuarto campo se olvida. Las claves son las del cable de `admin-web`
   * (`apps/admin-web/src/conversaciones.ts` §`leerFiltroDeMensajes`).
   */
  const campos: readonly (readonly [string, string | null])[] = [
    ["desde", filtro.desde],
    ["hasta", filtro.hasta],
    ["telefono", filtro.telefono],
    ["contenido", filtro.contenido],
  ]
  for (const [clave, valor] of campos) {
    if (valor !== null) url.searchParams.set(clave, valor)
  }
}

/**
 * `AW6` — arma la ruta de descarga con el filtro aplicado y SIN página.
 *
 * Es una función pura y exportada para que el examen la afirme directo: la ruta
 * apunta a `admin-web`, lleva ~~`desde`/`hasta`/`telefono`~~ ⭐ **los cuatro
 * campos** sólo cuando el campo no es `null`, y nunca lleva `page`/`pageSize`
 * (`P0-2` nombra el eje del FILTRO, no el de la paginación). El mismo orden de
 * escritura de parámetros que `listar`, porque **es el mismo escritor**.
 *
 * ⭐ `R3-UI1` · ~~`filtro.desde.toISOString()`~~ — los días viajan como días.
 * Además de perder el filtro Contenido, esta función **reventaba** con el filtro
 * de la pantalla de R3 (`filtro.desde.toISOString is not a function`): el chequeo
 * por EJECUCIÓN de `gates/r1/R3-UI1.gate.ts` lo encontró, y era RT 3 incumplido
 * también acá y no sólo en `filtros.tsx`, que es donde la etapa lo tenía anotado.
 */
export function construirRutaDeDescarga(raiz: string, filtro: FiltroDeLaPantalla): string {
  const url = new URL(`${raiz}/${RECURSO_DESCARGA_CONVERSACIONES}`)
  escribirElFiltro(url, filtro)
  return url.toString()
}

/**
 * ⭐ `AW30` — arma `POST {raiz}/conversaciones/{id_conversacion}/cierre`.
 *
 * Pura y exportada por el mismo motivo que `construirRutaDeDescarga`: el examen
 * la afirma directo, sin montar nada.
 *
 * ⛔ **El identificador va percent-encodeado.** El router de `admin-web` parte la
 * ruta en segmentos y le pasa cada uno por `decodeURIComponent`, y contesta `400`
 * si el segmento no decodifica. Un `id` con `/` o `%` armado a mano se leería
 * como otra ruta o como un pedido mal formado; `IdConversacion` es opaco y nadie
 * de este lado puede afirmar qué caracteres trae.
 */
export function construirRutaDeCierre(raiz: string, id_conversacion: IdConversacion): string {
  return `${raiz}/${RECURSO_CONVERSACIONES}/${encodeURIComponent(id_conversacion)}/${SEGMENTO_CIERRE}`
}

/** El cuerpo del cierre, tal como viaja. `id_sesion` es `null` cuando no cerró nada. */
type CierreEnLaRed = { readonly id_sesion: string | null }

export function crearBordeConversacionesHttp(config: ConfiguracionInstancia): BordeConversaciones {
  /**
   * ⭐ `AR5-m` · ⟦el-brazo-entra-por-ruta⟧ · **la raíz lleva el brazo adelante**, y
   * es una FUNCIÓN y no una constante porque el brazo sale de la dirección, que
   * cambia sin que este borde se vuelva a construir (`App.tsx` lo memoiza por
   * `config`). Ver `brazo-del-cable.ts`, que tiene la decisión del dueño citada.
   */
  const raiz = (): string => raizDelBrazo(config.adminWebUrl)

  return {
    listar: async (consulta: ConsultaDeLaPantalla): Promise<ResultadoDeMensajes> => {
      const url = new URL(`${raiz()}/${RECURSO_CONVERSACIONES}`)

      /**
       * Orden de escritura de los parámetros de query. `page`/`pageSize` van
       * primero; el filtro sólo se manda cuando el campo no es `null`.
       *
       * El criterio «sin filtro se escribe, no se omite» es del lado de
       * `admin-web`; de este lado, `null` simplemente no se envía.
       *
       * ⭐ `R3-UI1` · el filtro lo escribe `escribirElFiltro`, **la misma función
       * que la descarga**: ver ⟦el-filtro-se-escribe-una-vez⟧. Acá había tres `if`
       * gemelos de los de allá, y cuando `R3-CCV1` sumó el cuarto campo, allá se
       * quedaron en tres.
       */
      url.searchParams.set("page", String(consulta.pagina))
      url.searchParams.set("pageSize", String(consulta.tamanoDePagina))
      escribirElFiltro(url, consulta.filtro)

      let respuesta: Response
      try {
        respuesta = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          /**
           * La sesión la maneja `admin-web` (Better Auth + MFA, ADR-0006) y
           * viaja en una cookie que el navegador guarda y manda solo.
           *
           * `include` y no un `Authorization` armado acá: un token que la
           * pantalla tuviera que leer para poner en un header es un token que la
           * pantalla tuvo que GUARDAR en algún lado, y esta unidad **no guarda
           * nada, ni credenciales ni estado**. Con cookie `HttpOnly` el
           * JavaScript de la pantalla nunca la ve.
           */
          credentials: "include",
        })
      } catch (causa) {
        throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
          cause: causa,
        })
      }

      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `El servidor respondió ${respuesta.status} al listar mensajes.`,
          ),
          respuesta.status,
        )
      }

      const cuerpo = (await respuesta.json()) as ResultadoEnLaRed

      /**
       * `total` sale del borde, SIEMPRE. Nunca `items.length`.
       *
       * Es el total en la BASE para ese filtro; `items.length` es cuántas filas
       * entraron en esta página. Con `items.length` el paginado mentiría.
       */
      return {
        items: cuerpo.items.map(revivir),
        total: cuerpo.total,
      }
    },

    /**
     * ⭐ `R4-UIC1` · `AW6` — la descarga ya NO es una navegación de documento
     * (`window.location.assign`): usa la primitiva `descargarArchivo` de `R4-UI0`,
     * que hace `fetch` con la cookie de sesión y entrega el archivo por
     * `<a download>`. Así el botón sabe cuándo terminó («Descargando…», CA-13) y
     * cuándo falló (el aviso propio, CA-14), cosa que una navegación no permitía.
     *
     * La ruta es la de siempre —`construirRutaDeDescarga`, que manda el filtro por
     * `escribirElFiltro`, el único escritor (R3-F1-CA-67)— y el nombre del archivo
     * lo sigue diciendo `admin-web` por `Content-Disposition`: la pantalla no arma
     * ni `Blob` ni nombre de respaldo.
     */
    descargar: (filtro: FiltroDeLaPantalla): Promise<void> =>
      descargarArchivo(construirRutaDeDescarga(raiz(), filtro)),

    /**
     * ⭐⭐ `AW30` — el cierre. **`POST` sin cuerpo**, con la misma cookie que
     * `listar`.
     *
     * ⛔ **No lleva `body` ni `Content-Type`, y eso no es un ahorro.** El borde de
     * `admin-web` no lee ningún campo del cliente: el identificador va en la ruta y
     * el brazo lo resuelve la sesión. Mandar un cuerpo sería declarar un campo que
     * del otro lado no existe.
     *
     * ⚠️ El `id_sesion` vuelve **tal cual**, `null` incluido: ver el comentario del
     * método en `BordeConversaciones`. Acá no se decide qué significa.
     */
    cerrarSesionVigente: async (id_conversacion: IdConversacion): Promise<string | null> => {
      let respuesta: Response
      try {
        respuesta = await fetch(construirRutaDeCierre(raiz(), id_conversacion), {
          method: "POST",
          headers: { Accept: "application/json" },
          credentials: "include",
        })
      } catch (causa) {
        throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
          cause: causa,
        })
      }

      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `El servidor respondió ${respuesta.status} al cerrar la sesión.`,
          ),
          respuesta.status,
        )
      }

      const cuerpo = (await respuesta.json()) as CierreEnLaRed
      return cuerpo.id_sesion
    },
  }
}
